<template>
  <v-container fluid>
    <!-- Header Toolbar -->
    <v-toolbar
      flat
      color="white"
      rounded="lg"
      class="mb-4">
      <v-icon
        left
        color="green darken-2">
        mdi-chart-bar
      </v-icon>
      <v-toolbar-title class="font-weight-bold">
        ผลงานพนักงานคลังสินค้า
      </v-toolbar-title>
      <span class="text--secondary text-caption ml-2 hidden-sm-and-down">
        จำนวนงาน = QTY ต่อชั่วโมงทำงาน (ไม่นับเวลาพัก)
      </span>
      <v-spacer></v-spacer>
      <v-menu
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="auto">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="dateRangeText"
            label="ช่วงวันที่"
            prepend-inner-icon="mdi-calendar"
            readonly
            outlined
            dense
            hide-details
            style="max-width: 280px"
            v-bind="attrs"
            v-on="on">
          </v-text-field>
        </template>
        <v-date-picker
          v-model="dates"
          range
          no-title
          color="green darken-2"
          @input="onDateChange">
        </v-date-picker>
      </v-menu>
    </v-toolbar>

    <!-- Tabs -->
    <v-tabs
      v-model="activeTab"
      color="green darken-2"
      class="mb-4 rounded-lg">
      <v-tab>
        <v-icon
          left
          small>
          mdi-account-group
        </v-icon>
        ภาพรวมทีม
      </v-tab>
      <v-tab>
        <v-icon
          left
          small>
          mdi-account-search
        </v-icon>
        รายคน (Track รายบุคคล)
      </v-tab>
    </v-tabs>

    <v-tabs-items
      v-model="activeTab"
      class="pa-4">
      <!-- ===== TEAM TAB ===== -->
      <v-tab-item>
        <!-- Controls -->
        <v-row
          class="mb-3"
          align="center">
          <v-col
            cols="12"
            sm="auto">
            <v-select
              v-model="taskFilter"
              :items="taskFilterOptions"
              item-text="text"
              item-value="value"
              label="ประเภทงาน"
              outlined
              dense
              hide-details
              style="min-width: 200px">
            </v-select>
          </v-col>
        </v-row>

        <!-- KPI Cards -->
        <v-row class="mb-4">
          <v-col
            v-for="(kpi, i) in teamKpis"
            :key="i"
            cols="12"
            sm="6"
            md="3">
            <v-card
              rounded="lg"
              elevation="1"
              class="pa-4">
              <div class="overline text--secondary">
                {{ kpi.label }}
              </div>
              <div class="text-h5 font-weight-black mt-1">
                {{ kpi.value }}
                <span class="text-caption font-weight-regular text--secondary">{{ kpi.unit }}</span>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Ranking Table -->
        <v-card
          outlined
          rounded="lg"
          class="mb-6">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            ตารางจัดอันดับ
            <span class="text-caption font-weight-regular text--secondary ml-2">(คลิกแถวเพื่อดูรายคน)</span>
          </v-card-title>
          <v-data-table
            :headers="rankHeaders"
            :items="teamDataSorted"
            :items-per-page="10"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
            class="row-pointer"
            @click:row="openIndividual">
            <template v-slot:item.employee="{ item }">
              <div>
                <strong>{{ item.employee.firstName }} {{ item.employee.lastName }}</strong><br>
                <span class="text--secondary text-caption">{{ item.employee.employeeCode }}</span>
              </div>
            </template>
            <template v-slot:item.totalDuration="{ item }">
              {{ formatDuration(Math.round(item.totalDuration / 1000)) }}
            </template>
            <template v-slot:item.quantityPerHour="{ item }">
              <strong>{{ item.quantityPerHour.toFixed(1) }}</strong>
            </template>
          </v-data-table>
        </v-card>

        <!-- Employee Cards -->
        <div class="text-subtitle-1 font-weight-bold mb-3">
          การ์ดรายพนักงาน
          <span class="text-caption font-weight-regular text--secondary ml-1">(คลิกเพื่อดูรายละเอียด)</span>
        </div>
        <v-row>
          <v-col
            v-for="(emp, i) in teamDataByQph"
            :key="emp.id"
            cols="12"
            sm="6"
            md="2">
            <v-card
              outlined
              rounded="lg"
              class="pa-4"
              style="cursor: pointer; transition: .15s"
              @click="openIndividual(emp)">
              <div class="d-flex justify-space-between align-start mb-3">
                <div>
                  <div class="font-weight-bold">
                    {{ emp.employee.firstName }} {{ emp.employee.lastName }}
                  </div>
                  <div class="text--secondary text-caption">
                    {{ emp.employee.employeeCode }}
                  </div>
                </div>
                <v-chip
                  small
                  :color="i === 0 ? 'amber darken-1' : 'green lighten-4'"
                  :text-color="i === 0 ? 'white' : 'green darken-2'">
                  #{{ i + 1 }}
                </v-chip>
              </div>

              <div class="d-flex justify-space-between text-caption mb-1">
                <span class="text--secondary">จำนวนงาน/ชม.</span>
                <strong>{{ emp.quantityPerHour.toFixed(1) }}</strong>
              </div>
              <div class="d-flex justify-space-between text-caption mb-1">
                <span class="text--secondary">QTY รวม</span>
                <strong>{{ emp.totalQty }}</strong>
              </div>
              <div class="d-flex justify-space-between text-caption mb-3">
                <span class="text--secondary">เวลาทำงาน</span>
                <strong>{{ formatDuration(Math.round(emp.totalDuration / 1000)) }}</strong>
              </div>

              <v-progress-linear
                :value="(emp.quantityPerHour / maxQph) * 100"
                color="green darken-2"
                rounded
                height="6"
                class="mb-2">
              </v-progress-linear>

              <div class="d-flex flex-wrap mt-2">
                <v-chip
                  v-for="bt in emp.byTask"
                  :key="bt.taskType.name"
                  :color="deptColor(bt.taskType.department, bt.taskType.id)"
                  text-color="white"
                  x-small
                  class="mr-1 mb-1">
                  {{ bt.taskType.name }}: {{ bt.totalQty }}
                </v-chip>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- ===== INDIVIDUAL TAB ===== -->
      <v-tab-item>
        <v-row
          class="mb-4"
          align="center">
          <v-col
            cols="12"
            sm="auto">
            <v-select
              v-model="selectedEmpId"
              :items="empSelectOptions"
              item-text="label"
              item-value="id"
              label="เลือกพนักงาน"
              outlined
              dense
              hide-details
              style="min-width: 260px">
            </v-select>
          </v-col>
        </v-row>

        <template v-if="selectedEmp">
          <!-- Profile Header -->
          <v-card
            outlined
            rounded="lg"
            class="mb-4 pa-4">
            <div class="d-flex align-center">
              <v-avatar
                color="green darken-2"
                class="mr-4"
                size="52">
                <span class="white--text text-h6 font-weight-bold">{{ selectedEmp.employee.firstName[0] }}</span>
              </v-avatar>
              <div>
                <div class="text-h6 font-weight-bold">
                  {{ selectedEmp.employee.firstName }} {{ selectedEmp.employee.lastName }}
                </div>
                <div class="text--secondary text-caption">
                  {{ selectedEmp.employee.employeeCode }}
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-chip
                :color="selectedEmpRank === 1 ? 'amber darken-1' : 'green lighten-4'"
                :text-color="selectedEmpRank === 1 ? 'white' : 'green darken-2'">
                อันดับ #{{ selectedEmpRank }} จาก {{ teamDataByQph.length }}
              </v-chip>
            </div>
          </v-card>

          <!-- Individual KPI Cards -->
          <v-row class="mb-4">
            <v-col
              v-for="(kpi, i) in individualKpis"
              :key="i"
              cols="12"
              sm="6"
              md="3">
              <v-card
                rounded="lg"
                elevation="1"
                class="pa-4">
                <div class="overline text--secondary">
                  {{ kpi.label }}
                </div>
                <div class="text-h5 font-weight-black mt-1">
                  {{ kpi.value }}
                  <span class="text-caption font-weight-regular text--secondary">{{ kpi.unit }}</span>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Timeline -->
          <v-card
            outlined
            rounded="lg"
            class="mb-4 pa-4">
            <div class="text-subtitle-1 font-weight-bold mb-3">
              ไทม์ไลน์การทำงาน
            </div>
            <v-progress-linear
              v-if="empLogsLoading"
              indeterminate
              color="green darken-2"
              class="mb-3">
            </v-progress-linear>
            <div
              v-if="!empLogsLoading && !selectedEmpLogs.length"
              class="text-center text--secondary py-4 text-caption">
              ไม่มีข้อมูล session ในช่วงวันที่เลือก
            </div>
            <!-- Time axis (fixed 07:00 – 00:00) -->
            <div
              class="d-flex"
              style="padding-left: 130px; margin-bottom: 4px">
              <div style="flex: 1; position: relative; height: 18px; border-bottom: 1px solid #e0e0e0">
                <span
                  v-for="tick in timelineTicks"
                  :key="tick.label"
                  class="text--secondary"
                  :style="{
                    position: 'absolute',
                    left: tick.pct + '%',
                    transform: 'translateX(-50%)',
                    fontSize: '10px',
                    top: '1px'
                  }">
                  {{ tick.label }}
                </span>
              </div>
            </div>
            <div style="max-height: 400px; overflow-y: auto">
              <div
                v-for="day in timelineByDay"
                :key="day.date"
                class="mb-3">
                <!-- Session rows -->
                <div
                  v-for="(row, i) in day.rows"
                  :key="i"
                  class="d-flex align-center mb-2"
                  style="font-size: 12px">
                  <div style="width: 130px; flex-shrink: 0; color: #6b7773">
                    <template v-if="i === 0">
                      <strong style="color: #455a50">{{ day.date }}</strong><br>
                    </template>
                    {{ row.task }}<br>
                    <span style="font-size: 10px">{{ row.s }}–{{ row.e }}</span>
                  </div>
                  <div style="flex: 1; position: relative; height: 26px; background: #f3f5f4; border-radius: 6px; overflow: hidden">
                    <div
                      :style="{
                        position: 'absolute',
                        left: Math.max(dayTimelineLeft(row, day), 0) + '%',
                        width: Math.min(Math.max(dayTimelineWidth(row, day), 1), 100 - Math.max(dayTimelineLeft(row, day), 0)) + '%',
                        height: '100%',
                        background: deptColor(row.dept, row.taskId),
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: '600',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }"
                      :title="`${row.task} · ${row.d} ${row.s}–${row.e} · ${formatDuration(Math.round(row.dur / 1000))} · QTY ${row.qty}`">
                      {{ dayTimelineWidth(row, day) > 2.94 ? formatDurationShort(Math.round(row.dur / 1000)) : '' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="text-caption text--secondary mt-2">
              แท่ง = ระยะเวลาจริง (07:00–00:00) · เลื่อนเมาส์เพื่อดูรายละเอียด
            </div>
          </v-card>

          <!-- Charts -->
          <v-row
            v-if="selectedEmp && selectedEmp.totalQty > 0"
            class="mb-4">
            <v-col
              cols="12"
              md="7">
              <v-card
                outlined
                rounded="lg"
                class="pa-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  จำนวนงานต่อชั่วโมง แยกตามช่วงงาน
                </div>
                <session-bar-chart
                  v-if="sessionChartData.labels.length"
                  :chart-data="sessionChartData"
                  :height="170">
                </session-bar-chart>
                <div
                  v-if="!sessionChartData.labels.length && !empLogsLoading"
                  class="text-center text--secondary py-8 text-caption">
                  ไม่มีข้อมูล
                </div>
              </v-card>
            </v-col>
            <v-col
              cols="12"
              md="5">
              <v-card
                outlined
                rounded="lg"
                class="pa-4">
                <div class="text-subtitle-1 font-weight-bold mb-2">
                  QTY แยกตามประเภทงาน
                </div>
                <task-doughnut-chart
                  v-if="taskDoughnutData.labels.length"
                  :key="selectedEmpId"
                  :chart-data="taskDoughnutData"
                  :height="170">
                </task-doughnut-chart>
              </v-card>
            </v-col>
          </v-row>

          <!-- Session Log Table -->
          <v-card
            outlined
            rounded="lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">
              รายละเอียดทุกช่วงงาน (session log)
            </v-card-title>
            <v-data-table
              :headers="sessionHeaders"
              :items="selectedEmpLogs"
              :items-per-page="-1"
              hide-default-footer>
              <template v-slot:item.task="{ item }">
                <v-chip
                  small
                  :color="deptColor(item.dept, item.taskId)"
                  text-color="white">
                  {{ item.task }}
                </v-chip>
              </template>
              <template v-slot:item.dur="{ item }">
                {{ formatDuration(Math.round(item.dur / 1000)) }}
              </template>
              <template v-slot:item.quantityPerHour="{ item }">
                <strong>{{ (item.qty / (item.dur / 3600000)).toFixed(1) }}</strong>
              </template>
            </v-data-table>
          </v-card>
        </template>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import SessionBarChart from '../components/SessionBarChart'
import TaskDoughnutChart from '../components/TaskDoughnutChart'
import WarehouseWorkTrackingProvider from '@/resources/WarehouseWorkTrackingProvider'
import { mapActions } from 'vuex'

const provider = new WarehouseWorkTrackingProvider()

// fixed timeline scale: 07:00 – 00:00 (17 ชั่วโมง)
const TL_START_HOUR = 7
const TL_HOURS = 17
const TL_SPAN_SEC = TL_HOURS * 3600
const TL_TICKS = [7, 9, 11, 13, 15, 17, 19, 21, 23, 0].map((h) => ({
  label: h === 0 ? '00:00' : `${String(h).padStart(2, '0')}:00`,
  pct: (((h === 0 ? 24 : h) - TL_START_HOUR) / TL_HOURS) * 100
}))

// สีตาม department ของ task type
const DEPT_COLOR = {
  Inbound: '#3b82f6',
  Outbound: '#10b981',
  Inventory: '#8b5cf6',
  Return: '#f59e0b',
  Other: '#ec4899'
}

const BREAK_TASK_ID = '6a17c154b47181033467e67b'
const BREAK_COLOR = '#64748b'

export default {
  name: 'WarehouseEmployeePerformance',

  components: { SessionBarChart, TaskDoughnutChart },

  data () {
    const today = this.$dayjs().format('YYYY-MM-DD')
    const weekAgo = this.$dayjs().subtract(6, 'day').format('YYYY-MM-DD')
    return {
      loading: false,
      performanceData: [],
      activeTab: 0,
      dateMenu: false,
      dates: [weekAgo, today],
      taskFilter: 'all',
      allTaskTypes: [],
      selectedEmpId: null,
      empLogs: [],
      empLogsLoading: false
    }
  },

  computed: {
    dateRangeText () {
      if (this.dates.length !== 2) return ''
      return [...this.dates].sort().join(' ~ ')
    },

    sortedDates () {
      return [...this.dates].sort()
    },

    taskFilterOptions () {
      return [
        { text: 'ทุกประเภทงาน', value: 'all' },
        ...this.allTaskTypes.map((t) => ({ text: t.name, value: t.name }))
      ]
    },

    aggregated () {
      return this.performanceData
        .map((item) => {
          if (this.taskFilter === 'all') return item
          const bt = item.byTask.find((b) => b.taskType.name === this.taskFilter)
          if (!bt) return null
          return {
            ...item,
            totalQty: bt.totalQty,
            totalDuration: bt.totalDuration,
            totalTasks: bt.totalTasks,
            quantityPerHour: bt.totalDuration > 0
              ? parseFloat((bt.totalQty / (bt.totalDuration / 3600000)).toFixed(1))
              : 0
          }
        })
        .filter(Boolean)
    },

    teamDataSorted () {
      return [...this.aggregated]
        .filter((e) => e.totalDuration > 0)
        .sort((a, b) => b.quantityPerHour - a.quantityPerHour)
    },

    teamDataByQph () {
      return [...this.aggregated]
        .filter((e) => e.totalDuration > 0)
        .sort((a, b) => b.quantityPerHour - a.quantityPerHour)
    },

    maxQph () {
      return Math.max(...this.teamDataByQph.map((e) => e.quantityPerHour), 1)
    },

    teamKpis () {
      const data = this.teamDataSorted
      const totQty = data.reduce((s, e) => s + e.totalQty, 0)
      const totDur = data.reduce((s, e) => s + e.totalDuration, 0)
      const avgQph = totDur > 0 ? totQty / (totDur / 3600000) : 0
      return [
        { label: 'พนักงาน', value: data.length, unit: 'คน' },
        { label: 'QTY รวม', value: totQty.toLocaleString(), unit: 'ชิ้น' },
        { label: 'จำนวนงานเฉลี่ย', value: avgQph.toFixed(1), unit: '/ชม.' },
        { label: 'เวลาทำงานรวม', value: this.formatDuration(Math.round(totDur / 1000)), unit: '' }
      ]
    },

    rankHeaders () {
      return [
        { text: 'พนักงาน', value: 'employee', sortable: false },
        { text: 'QTY รวม', value: 'totalQty', align: 'right' },
        { text: 'เวลาทำงาน', value: 'totalDuration', align: 'right' },
        { text: 'จำนวนงาน/ชม.', value: 'quantityPerHour', align: 'right' },
        { text: 'จำนวนงาน', value: 'totalTasks', align: 'right' }
      ]
    },

    // empSelect ใช้ list จาก API โดยตรง (ไม่ขึ้นกับ taskFilter)
    empSelectOptions () {
      return this.performanceData.map((item) => ({
        id: item.employee.id,
        label: `${item.employee.firstName} ${item.employee.lastName} (${item.employee.employeeCode})`
      }))
    },

    selectedEmp () {
      if (!this.selectedEmpId) return null
      return this.performanceData.find((item) => item.employee.id === this.selectedEmpId) || null
    },

    selectedEmpRank () {
      return this.performanceData.findIndex((item) => item.employee.id === this.selectedEmpId) + 1
    },

    individualKpis () {
      if (!this.selectedEmp) return []
      return [
        { label: 'จำนวนงาน/ชม.', value: this.selectedEmp.quantityPerHour.toFixed(1), unit: '' },
        { label: 'QTY รวม', value: this.selectedEmp.totalQty, unit: 'ชิ้น' },
        { label: 'เวลาทำงาน', value: this.formatDuration(Math.round(this.selectedEmp.totalDuration / 1000)), unit: '' },
        { label: 'จำนวนงาน', value: this.selectedEmp.totalTasks, unit: 'session' }
      ]
    },

    taskDoughnutData () {
      if (!this.selectedEmp) return { labels: [], datasets: [] }
      const byTask = this.selectedEmp.byTask.filter((b) => b.totalQty > 0)
      return {
        labels: byTask.map((b) => b.taskType.name),
        datasets: [{
          data: byTask.map((b) => b.totalQty),
          backgroundColor: byTask.map((b) => DEPT_COLOR[b.taskType.department] || DEPT_COLOR.Other)
        }]
      }
    },

    // session-level data จาก GET /logs
    selectedEmpLogs () {
      return this.empLogs
    },

    timelineTicks () {
      return TL_TICKS
    },

    // group logs ตามวัน — fixed scale 07:00–00:00 ทุกวันเหมือนกัน
    timelineByDay () {
      if (!this.empLogs.length) return []
      const groups = {}
      this.empLogs.forEach((row) => {
        if (!groups[row.d]) groups[row.d] = []
        groups[row.d].push(row)
      })
      return Object.keys(groups).sort().map((date) => {
        const rows = groups[date]
        // t0 = 07:00 ของวันนั้น (epoch seconds, local time)
        const t0 = this.$dayjs(date).startOf('day').unix() + (TL_START_HOUR * 3600)
        return { date, rows, t0, span: TL_SPAN_SEC }
      })
    },

    sessionChartData () {
      if (!this.empLogs.length) return { labels: [], datasets: [] }
      return {
        labels: this.empLogs.map((r) => `${r.d} ${r.s}`),
        datasets: [{
          label: 'จำนวนงาน/ชม.',
          data: this.empLogs.map((r) => (r.dur > 0
            ? parseFloat((r.qty / (r.dur / 3600000)).toFixed(1))
            : 0)),
          backgroundColor: this.empLogs.map((r) => this.deptColor(r.dept, r.taskId))
        }]
      }
    },

    sessionHeaders () {
      return [
        { text: 'วันที่', value: 'd' },
        { text: 'ประเภทงาน', value: 'task' },
        { text: 'เริ่ม', value: 's' },
        { text: 'สิ้นสุด', value: 'e' },
        { text: 'ระยะเวลา', value: 'dur', align: 'right' },
        { text: 'QTY', value: 'qty', align: 'right' },
        { text: 'จำนวนงาน/ชม.', value: 'quantityPerHour', align: 'right' }
      ]
    }
  },

  watch: {
    selectedEmpId (val) {
      if (val) this.fetchEmpLogs()
    },
    dates (val) {
      if (val.length === 2 && this.selectedEmpId) this.fetchEmpLogs()
    }
  },

  mounted () {
    this.fetchTaskTypes()
    this.fetchPerformance()
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    async fetchTaskTypes () {
      try {
        const { data } = await provider.getTaskTypes({ page: 1, limit: 999, sortBy: 'name', sortOrder: 'asc' })
        this.allTaskTypes = (data.results || []).filter((t) => t.status === 'active')
      } catch (err) {
        console.error('Failed to fetch task types:', err)
      }
    },

    normalizeLog (log) {
      const start = this.$dayjs(log.startAt)
      const end = log.endAt ? this.$dayjs(log.endAt) : null
      const tt = log.taskTypeId || {}
      return {
        taskId: tt.id,
        task: tt.name || 'ไม่ระบุ',
        dept: tt.department || 'Other',
        d: start.format('YYYY-MM-DD'),
        s: start.format('HH:mm'),
        e: end ? end.format('HH:mm') : '-',
        s0: start.unix(),
        dur: log.duration || 0,
        qty: log.quantity || 0
      }
    },

    async fetchEmpLogs () {
      if (!this.selectedEmpId || this.sortedDates.length !== 2) return
      this.empLogsLoading = true
      this.empLogs = []
      try {
        const [startDate, endDate] = this.sortedDates
        const { data } = await provider.getWorkLogsByPaginate({
          employeeId: this.selectedEmpId,
          startDate,
          endDate,
          state: 'completed',
          page: 1,
          limit: 500,
          sortBy: 'startAt',
          sortOrder: 'asc'
        })
        this.empLogs = (data.results || []).map(this.normalizeLog)
      } catch (err) {
        console.error('Failed to fetch employee logs:', err)
        this.setSnackbar({
          value: true,
          message: 'ไม่สามารถโหลดข้อมูล session ได้',
          type: 'error'
        })
      } finally {
        this.empLogsLoading = false
      }
    },

    async fetchPerformance () {
      if (this.dates.length !== 2) return
      this.loading = true
      try {
        const [startDate, endDate] = this.sortedDates
        const { data } = await provider.getReportPerformance({ startDate, endDate })
        this.performanceData = data || []
        // auto-select พนักงานอันดับ 1 ถ้ายังไม่ได้เลือก
        if (this.performanceData.length && !this.selectedEmpId) {
          this.selectedEmpId = this.performanceData[0].employee.id
        }
      } catch (err) {
        console.error('Failed to fetch performance data:', err)
        this.setSnackbar({
          value: true,
          message: 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    formatDurationShort (s) {
      if (!s) return '-'
      const h = Math.floor(s / 3600)
      const m = Math.round((s % 3600) / 60)
      return h > 0 ? `${h}h ${m}m` : `${m}m`
    },

    formatDuration (s) {
      if (!s) return '-'
      const h = Math.floor(s / 3600)
      const m = Math.round((s % 3600) / 60)
      return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`
    },

    deptColor (dept, taskId) {
      if (taskId === BREAK_TASK_ID) return BREAK_COLOR
      return DEPT_COLOR[dept] || DEPT_COLOR.Other
    },

    dayTimelineLeft (row, day) {
      const offset = Math.max(row.s0 - day.t0, 0)
      return (offset / day.span) * 100
    },

    dayTimelineWidth (row, day) {
      const visibleStart = Math.max(row.s0, day.t0)
      const visibleDur = (row.s0 + (row.dur / 1000)) - visibleStart
      return (Math.max(visibleDur, 0) / day.span) * 100
    },

    onDateChange () {
      if (this.dates.length === 2) {
        this.dateMenu = false
        this.fetchPerformance()
      }
    },

    openIndividual (emp) {
      this.activeTab = 1
      this.$nextTick(() => {
        this.selectedEmpId = emp.employee.id
      })
    }
  }
}
</script>

<style scoped>
.row-pointer >>> tbody tr {
  cursor: pointer;
}
.v-data-table >>> thead th {
  background-color: #fafafa !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>
